import { createTimeline, stagger, splitText } from '../../vendor/anime.esm.js';
import { resolveDuration, DURATION, EASE, SPRING_PRESETS, BLUR } from '../motion-tokens.js';
import { getMotionComplexity } from '../variant-animations.js';
import { TYPED_DONE } from '../typed.js';

/**
 * ¿El titular lo gobierna el efecto máquina de escribir? Entonces esta coreografía NO lo toca:
 * el tecleo vacía el <h1> y lo reescribe carácter a carácter, así que cualquier apagado previo
 * (opacity:0) se quedaría sin encender y el texto no se vería hasta el rescate de los 6 s.
 *
 * La marca viene de animations/typed.js (SSOT). Se mantiene además el `closest('[data-typed]')`
 * para un contenedor marcado a mano, que es lo único que hacía la comprobación anterior... solo
 * que preguntaba por `data-typed` en el propio título, atributo que no escribe nadie.
 */
const tituloTecleado = (title) =>
    !!(title && (title.dataset[TYPED_DONE] || title.closest('[data-typed]')));

export const heroV1 = (el) => {
    const tl = createTimeline({ defaults: { ease: EASE.refined } });
    const title    = el.querySelector('h1, h2');
    const isTyped = tituloTecleado(title);
    const subtitle = el.querySelector('p, .subtitle');
    // Con botón secundario entra la FILA de los dos (`.n3i-botones`, que va antes que sus botones en el
    // orden del documento); sin él, el botón de siempre.
    const cta      = el.querySelector('.n3i-botones, .btn-action, [data-field="cta"], .btn, .cta-btn');

    if (title && !isTyped) { title.style.opacity = '0'; title.style.transform = 'translateX(-60px)'; title.style.filter = BLUR.heavy; }
    if (subtitle) { subtitle.style.opacity = '0'; subtitle.style.transform = 'translateY(20px)'; }
    if (cta) { cta.style.opacity = '0'; cta.style.transform = 'translateY(25px) scale(0.9)'; }

    if (title && !isTyped) tl.add(title, {
        translateX: [-60, 0],
        opacity:    [0, 1],
        filter:     [BLUR.heavy, BLUR.none],
        duration:   resolveDuration(DURATION.dramatic),
    });

    if (subtitle) tl.add(subtitle, {
        opacity:    [0, 1],
        translateY: [20, 0],
        filter:     [BLUR.normal, BLUR.none],
        duration:   resolveDuration(DURATION.slow),
    }, '-=800');

    if (cta) tl.add(cta, {
        opacity:    [0, 1],
        translateY: [25, 0],
        scale:      [0.9, 1],
        duration:   resolveDuration(DURATION.normal),
        ease:       SPRING_PRESETS.magnetic,
    }, '-=400');

    return tl;
};

export const heroV2 = (el) => {
    const tl = createTimeline();
    const title    = el.querySelector('h1, h2');
    const isTyped = tituloTecleado(title);
    const subtitle = el.querySelector('p, .subtitle');
    // Con botón secundario entra la FILA de los dos (`.n3i-botones`, que va antes que sus botones en el
    // orden del documento); sin él, el botón de siempre.
    const cta      = el.querySelector('.n3i-botones, .btn-action, [data-field="cta"], .btn, .cta-btn');

    if (title && !isTyped) { title.style.opacity = '0'; title.style.filter = BLUR.heavy; }
    if (subtitle) { subtitle.style.opacity = '0'; subtitle.style.transform = 'translateY(15px)'; }
    if (cta) { cta.style.opacity = '0'; cta.style.transform = 'scale(0.92)'; }

    if (title && !isTyped) {
        // El apagado de arriba (opacity:0 + blur) es sobre el <h1>. Cuando el troceado por
        // caracteres funciona, quien se enciende son los <span> que crea splitText... y el
        // <h1> se queda a 0 salvo que lo encendamos nosotros. Por eso el tween del titular
        // entero es también el RESPALDO de los dos caminos en que el troceado no sale:
        // que splitText lance, o que devuelva 0 caracteres. Sin él, el titular quedaba
        // invisible hasta que la red de rescate lo destapaba 6 s después.
        const animarTituloEntero = () => tl.add(title, {
            opacity:    [0, 1],
            translateY: [20, 0],
            filter:     [BLUR.heavy, BLUR.none],
            duration:   resolveDuration(DURATION.slow),
            ease:       EASE.refined,
        });

        let animadoPorCaracteres = false;
        if (getMotionComplexity() === 'full') {
            try {
                const split = splitText(title, { lines: false, words: false, characters: true });
                if (split.characters?.length) {
                    tl.add(split.characters, {
                        opacity:    [0, 1],
                        translateX: () => (Math.random() * 240 - 120),
                        translateY: () => (Math.random() * 100 - 50),
                        rotate:     () => (Math.random() * 50 - 25),
                        transformOrigin: '50% 50%',
                        filter:     [BLUR.normal, BLUR.none],
                        delay:      stagger(32, { from: 'center', start: 200 }),
                        duration:   resolveDuration(DURATION.dramatic),
                        ease:       SPRING_PRESETS.magnetic,
                    });
                    // splitText envuelve el texto en spans: el <h1> ya no se apaga solo, hay
                    // que devolverlo a visible para que se vean sus caracteres.
                    title.style.opacity = '';
                    title.style.filter = '';
                    animadoPorCaracteres = true;
                }
            } catch (e) {
                console.warn('[hero-v2] splitText falló; se anima el titular entero:', e);
            }
        }
        if (!animadoPorCaracteres) animarTituloEntero();
    }

    if (subtitle) tl.add(subtitle, {
        opacity:    [0, 1],
        translateY: [15, 0],
        filter:     [BLUR.normal, BLUR.none],
        duration:   resolveDuration(DURATION.slow),
        ease:       EASE.refined,
    }, '-=200');

    if (cta) tl.add(cta, {
        opacity:    [0, 1],
        scale:      [0.92, 1],
        duration:   resolveDuration(DURATION.normal),
        ease:       SPRING_PRESETS.magnetic,
    }, '-=300');

    return tl;
};

export const heroV3 = (el) => {
    const tl = createTimeline({ defaults: { ease: EASE.refined } });
    const maskPath = el.querySelector('.hero-mask-path');
    const image = el.querySelector('img, video, .hero-media, .unsplash-target');
    const title = el.querySelector('h1, h2');
    const isTyped = tituloTecleado(title);

    if (title && !isTyped) { title.style.opacity = '0'; title.style.transform = 'translateX(-65px)'; title.style.filter = 'blur(4px)'; }
    if (image) { image.style.opacity = '0'; image.style.transform = 'scale(1.26) rotate(4deg)'; }

    if (title && !isTyped) tl.add(title, {
        translateX: [-65, 0],
        opacity:    [0, 1],
        filter:     ['blur(4px)', 'blur(0px)'],
        duration:   resolveDuration(DURATION.dramatic),
    }, 0);

    if (maskPath) {
        tl.add(maskPath, {
            d: [
                'M 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 C 0.5 0.5, 0.5 0.5, 0.5 0.5 Z',
                'M 0.5 0.05 C 0.85 0.05, 0.95 0.3, 0.95 0.65 C 0.95 0.95, 0.7 0.95, 0.5 0.95 C 0.15 0.95, 0.05 0.75, 0.05 0.5 C 0.05 0.15, 0.25 0.05, 0.5 0.05 Z',
                'M 0 0 C 1 0, 1 0, 1 0 C 1 1, 1 1, 1 1 C 0 1, 0 1, 0 1 C 0 0, 0 0, 0 0 Z'
            ],
            duration: 1600,
            ease: SPRING_PRESETS.cinematic
        }, 100);
    }

    if (image) {
        tl.add(image, {
            scale:      [1.26, 1],
            rotate:     [4, 0],
            opacity:    [0, 1],
            duration:   1800,
            ease:       SPRING_PRESETS.cinematic,
        }, 120);
    }

    return tl;
};
